import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import {
  draftContactContentSchema,
  type DraftContactContent,
  type DraftSocialLink,
  type SocialPlatform,
} from "@profolio/validators";

interface ContactEditorProps {
  content: Record<string, unknown>;
  onChange: (content: DraftContactContent) => void;
}

export function ContactEditor({ content, onChange }: ContactEditorProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const parseResult = draftContactContentSchema.safeParse(content);
  const data: DraftContactContent = parseResult.success
    ? parseResult.data
    : {
        heading: "Contact & Profiles",
        email: "",
        phone: "",
        office: "",
        address: "",
        website: "",
        showContactForm: false,
        socialLinks: [],
      };

  const heading = data.heading || "Contact & Profiles";
  const email = data.email || "";
  const phone = data.phone || "";
  const office = data.office || "";
  const address = data.address || "";
  const website = data.website || "";
  const socialLinks: DraftSocialLink[] = data.socialLinks || [];

  const notifyChange = (
    newHeading: string,
    newEmail: string,
    newPhone: string,
    newOffice: string,
    newAddress: string,
    newWebsite: string,
    newLinks: DraftSocialLink[]
  ) => {
    onChange({
      heading: newHeading,
      email: newEmail,
      phone: newPhone,
      office: newOffice,
      address: newAddress,
      website: newWebsite,
      showContactForm: false,
      socialLinks: newLinks,
    });
  };

  const addSocialLink = () => {
    if (socialLinks.length >= 20) return;
    const updated: DraftSocialLink[] = [
      ...socialLinks,
      { platform: "scholar", url: "", label: "" },
    ];
    setFocusedIndex(socialLinks.length);
    notifyChange(heading, email, phone, office, address, website, updated);
  };

  const updateSocialLink = (
    index: number,
    field: keyof DraftSocialLink,
    value: string
  ) => {
    const updated = [...socialLinks];
    updated[index] = { ...updated[index], [field]: value };
    notifyChange(heading, email, phone, office, address, website, updated);
  };

  const removeSocialLink = (index: number) => {
    const updated = socialLinks.filter((_, i) => i !== index);
    notifyChange(heading, email, phone, office, address, website, updated);
  };

  return (
    <div className="space-y-6">
      {/* ── 1. Heading & Primary Contact ── */}
      <div>
        <Label htmlFor="contact-heading" className="text-xs font-semibold">
          Section Heading
        </Label>
        <Input
          id="contact-heading"
          value={heading}
          onChange={(e) =>
            notifyChange(e.target.value, email, phone, office, address, website, socialLinks)
          }
          placeholder="Contact & Profiles"
          className="mt-1 text-xs"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="contact-email" className="text-xs font-semibold">
            Institutional / Public Email *
          </Label>
          <Input
            id="contact-email"
            type="email"
            value={email}
            onChange={(e) =>
              notifyChange(heading, e.target.value, phone, office, address, website, socialLinks)
            }
            placeholder="e.g. janedoe@university.ac.lk"
            className="mt-1 text-xs"
          />
        </div>

        <div>
          <Label htmlFor="contact-office" className="text-xs font-semibold">
            Office Location / Room Number
          </Label>
          <Input
            id="contact-office"
            value={office}
            onChange={(e) =>
              notifyChange(heading, email, phone, e.target.value, address, website, socialLinks)
            }
            placeholder="e.g. Room 402, Department of Computer Science"
            className="mt-1 text-xs"
          />
        </div>
      </div>

      {/* ── 2. Progressive Disclosure: Additional Contact Channels ── */}
      <div className="rounded-lg border bg-card/60 overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen(!detailsOpen)}
          className="flex w-full items-center justify-between p-3 text-xs font-semibold hover:bg-muted/40 transition-colors"
        >
          <span>Phone, Personal Website & Postal Address</span>
          {detailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {detailsOpen && (
          <div className="p-4 pt-1 border-t space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="contact-phone" className="text-xs font-medium">
                  Office Phone / Extension
                </Label>
                <Input
                  id="contact-phone"
                  value={phone}
                  onChange={(e) =>
                    notifyChange(heading, email, e.target.value, office, address, website, socialLinks)
                  }
                  placeholder="e.g. +94 11 258 1245"
                  className="mt-1 text-xs"
                />
              </div>

              <div>
                <Label htmlFor="contact-website" className="text-xs font-medium">
                  Personal / Lab Website
                </Label>
                <Input
                  id="contact-website"
                  value={website}
                  onChange={(e) =>
                    notifyChange(heading, email, phone, office, address, e.target.value, socialLinks)
                  }
                  placeholder="https://janedoe-lab.org"
                  className="mt-1 text-xs"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="contact-address" className="text-xs font-medium">
                Postal / Physical Campus Address
              </Label>
              <Input
                id="contact-address"
                value={address}
                onChange={(e) =>
                  notifyChange(heading, email, phone, office, e.target.value, website, socialLinks)
                }
                placeholder="e.g. Faculty of Science, University of Colombo, Reid Avenue, Colombo 00700"
                className="mt-1 text-xs"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Academic & Social Profiles ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Academic & Social Profiles ({socialLinks.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Google Scholar, ORCID, ResearchGate, GitHub, LinkedIn, and more.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addSocialLink}
            disabled={socialLinks.length >= 20}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Profile Link
          </Button>
        </div>

        {socialLinks.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
            No profile links added yet. Click "Add Profile Link" to connect your Google Scholar, ORCID, or GitHub.
          </div>
        ) : (
          <div className="space-y-2.5">
            {socialLinks.map((link, index) => (
              <div
                key={link.id || index}
                className="flex items-center gap-2 rounded-lg border bg-card p-3 shadow-2xs"
              >
                <div className="w-36 shrink-0">
                  <Select
                    value={link.platform}
                    onChange={(e) =>
                      updateSocialLink(
                        index,
                        "platform",
                        e.target.value as SocialPlatform
                      )
                    }
                    className="text-xs bg-card text-foreground"
                  >
                    <option value="scholar" className="bg-card text-foreground">Google Scholar</option>
                    <option value="orcid" className="bg-card text-foreground">ORCID</option>
                    <option value="researchgate" className="bg-card text-foreground">ResearchGate</option>
                    <option value="linkedin" className="bg-card text-foreground">LinkedIn</option>
                    <option value="github" className="bg-card text-foreground">GitHub</option>
                    <option value="twitter" className="bg-card text-foreground">X / Twitter</option>
                    <option value="youtube" className="bg-card text-foreground">YouTube</option>
                    <option value="facebook" className="bg-card text-foreground">Facebook</option>
                    <option value="instagram" className="bg-card text-foreground">Instagram</option>
                    <option value="website" className="bg-card text-foreground">Custom Website</option>
                    <option value="other" className="bg-card text-foreground">Other</option>
                  </Select>
                </div>

                <div className="flex-1">
                  <Input
                    placeholder="Profile URL (https://...)"
                    value={link.url}
                    autoFocus={focusedIndex === index}
                    onFocus={() => setFocusedIndex(null)}
                    onChange={(e) => updateSocialLink(index, "url", e.target.value)}
                    className="text-xs"
                  />
                </div>

                <div className="w-28 shrink-0">
                  <Input
                    placeholder="Label (optional)"
                    value={link.label || ""}
                    onChange={(e) => updateSocialLink(index, "label", e.target.value)}
                    className="text-xs"
                  />
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeSocialLink(index)}
                  className="h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
